import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { TipoArea } from '../models/scope';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { LABEL_AREA } from '../utils/labels';

interface ModalGerenciarAreasProcessosProps {
  aberto: boolean;
  onFechar: () => void;
}

const TIPOS_AREA: TipoArea[] = [
  'TI', 'RH', 'FOLHA_PAGAMENTO', 'PATRIMONIO', 'ESTOQUES', 'TESOURARIA', 'CONTAS_PAGAR',
  'CONTAS_RECEBER', 'COMERCIAL', 'FATURAMENTO', 'FISCAL', 'TRIBUTOS_DIRETOS', 'TRIBUTOS_INDIRETOS',
  'OBRIGACOES_ACESSORIAS', 'SOCIETARIO', 'CONTABILIDADE', 'CONTROLADORIA', 'LANCAMENTOS_MANUAIS',
  'CONCILIACAO_CONTABIL', 'ANALISE_RESULTADOS', 'REVISAO_CONTABIL', 'WORKFLOW_SOX', 'OUTRAS',
];

// Etapa de setup AREAS_PROCESSOS (RN-19/RN-18) — cadastro real de áreas e processos do fechamento,
// não só o texto de evidência. Backend já tinha CRUD completo (MaestroAreaController/
// MaestroProcessoController), só faltava a tela.
const ModalGerenciarAreasProcessos: React.FC<ModalGerenciarAreasProcessosProps> = ({ aberto, onFechar }) => {
  const { areas, empresasEscopo, usuarios, recarregar } = useMaestroContext();
  const [processos, setProcessos] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [nomeArea, setNomeArea] = useState('');
  const [tipoArea, setTipoArea] = useState<TipoArea>('CONTABILIDADE');
  const [responsavelArea, setResponsavelArea] = useState(0);
  const [empresasArea, setEmpresasArea] = useState<number[]>([]);
  const [enviandoArea, setEnviandoArea] = useState(false);

  const [nomeProcesso, setNomeProcesso] = useState('');
  const [areaIdProcesso, setAreaIdProcesso] = useState(0);
  const [responsavelProcesso, setResponsavelProcesso] = useState(0);
  const [enviandoProcesso, setEnviandoProcesso] = useState(false);

  const carregar = () => {
    setCarregando(true);
    maestroService.listarProcessos().then(setProcessos).catch(() => setProcessos([])).finally(() => setCarregando(false));
  };

  useEffect(() => { if (aberto) carregar(); }, [aberto]);

  const alternarEmpresaArea = (id: number) =>
    setEmpresasArea(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  const criarArea = () => {
    if (!nomeArea.trim() || empresasArea.length === 0) return;
    setEnviandoArea(true);
    maestroService.criarArea({ tipo: tipoArea, nome: nomeArea.trim(), responsavelId: responsavelArea || undefined, empresaIds: empresasArea })
      .then(() => { setNomeArea(''); setEmpresasArea([]); recarregar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar a área.'))
      .finally(() => setEnviandoArea(false));
  };

  const criarProcesso = () => {
    if (!nomeProcesso.trim() || !areaIdProcesso) return;
    setEnviandoProcesso(true);
    maestroService.criarProcesso({ areaId: areaIdProcesso, nome: nomeProcesso.trim(), responsavelId: responsavelProcesso || undefined, empresaIds: empresasEscopo.map(e => e.id) })
      .then(() => { setNomeProcesso(''); setAreaIdProcesso(0); carregar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o processo.'))
      .finally(() => setEnviandoProcesso(false));
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='md' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Áreas e Processos do Fechamento</DialogTitle>
      <DialogContent>
        <Card_ titulo='Áreas cadastradas'>
          {areas.length === 0
            ? <p className='mc-texto-neutro'>Nenhuma área cadastrada ainda.</p>
            : <ul className='mc-wizard-lista'>{areas.map(a => <li key={a.id}>{LABEL_AREA[a.tipo]} — {a.nome}</li>)}</ul>}

          <div className='mc-form-inline'>
            <label>Tipo
              <select value={tipoArea} onChange={e => setTipoArea(e.target.value as TipoArea)}>
                {TIPOS_AREA.map(t => <option key={t} value={t}>{LABEL_AREA[t]}</option>)}
              </select>
            </label>
            <label>Nome da área<input type='text' value={nomeArea} onChange={e => setNomeArea(e.target.value)} placeholder='Ex.: Contabilidade Societária' /></label>
            <label>Responsável
              <select value={responsavelArea} onChange={e => setResponsavelArea(Number(e.target.value))}>
                <option value={0}>Sem responsável</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <small className='mc-texto-neutro' style={{ display: 'block', marginBottom: 6 }}>Empresas desta área</small>
            {empresasEscopo.map(emp => (
              <label key={emp.id} className='mc-checkbox' style={{ marginRight: 16 }}>
                <input type='checkbox' checked={empresasArea.includes(emp.id)} onChange={() => alternarEmpresaArea(emp.id)} /> {emp.nome}
              </label>
            ))}
          </div>
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={enviandoArea || !nomeArea.trim() || empresasArea.length === 0} onClick={criarArea}>
            {enviandoArea ? 'Criando...' : 'Adicionar área'}
          </button>
        </Card_>

        <Card_ titulo='Processos cadastrados'>
          {carregando
            ? <p className='mc-texto-neutro'>Carregando...</p>
            : processos.length === 0
              ? <p className='mc-texto-neutro'>Nenhum processo cadastrado ainda.</p>
              : <ul className='mc-wizard-lista'>{processos.map(p => <li key={p.id}>{p.nome} — {areas.find(a => a.id === p.areaId)?.nome ?? `área #${p.areaId}`} ({p.status})</li>)}</ul>}

          <div className='mc-form-inline'>
            <label>Área
              <select value={areaIdProcesso} onChange={e => setAreaIdProcesso(Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
            </label>
            <label>Nome do processo<input type='text' value={nomeProcesso} onChange={e => setNomeProcesso(e.target.value)} placeholder='Ex.: Conciliação bancária' /></label>
            <label>Responsável
              <select value={responsavelProcesso} onChange={e => setResponsavelProcesso(Number(e.target.value))}>
                <option value={0}>Sem responsável</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </label>
            <button type='button' className='mc-btn mc-btn--primary' disabled={enviandoProcesso || !nomeProcesso.trim() || !areaIdProcesso} onClick={criarProcesso}>
              {enviandoProcesso ? 'Criando...' : 'Adicionar processo'}
            </button>
          </div>
        </Card_>
      </DialogContent>
    </Dialog>
  );
};

const Card_: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#181c32', marginBottom: 12 }}>{titulo}</h3>
    {children}
  </div>
);

export default ModalGerenciarAreasProcessos;
